import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useTranslation } from "react-i18next";

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "shared/components/ui/alert-dialog";
import { Input } from "shared/components/ui/input";
import { Label } from "shared/components/ui/label";
import { AssistantService } from "shared/services/AssistantService";
import {
  ReportSectionsTree,
  ReportSections
} from "./ReportSectionsTree";
import { FinecoButton } from "pages/assistant/components/FinecoButton";
import colorsStore from "shared/store/colorsStore";
import useThreadsStore from "shared/store/threadsStore";

type GenerateReportProps = {
  artifact: {
    financial_plan: number;
    risk_profile: string;
  };
  threadId?: string;
  portfolioId?: string;
  portfolioData?: any;
  children: React.ReactNode;
  onGenerate: (data: any, fromReport?: boolean) => void;
};

export const GenerateReport = forwardRef<
  { generate: () => void },
  GenerateReportProps
>(({ artifact, threadId, portfolioId, portfolioData, children, onGenerate }, ref) => {
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);

  const { t } = useTranslation();
  const [bankerName, setBankerName] = useState("");
  const [bankerRole, setBankerRole] = useState("");
  const [proposedValue, setProposedValue] = useState(100000);
  const [reportSections, setReportSections] = useState<ReportSections>({});

  // Access story from global store
  const stories = useThreadsStore((state) => state.stories);

  useImperativeHandle(ref, () => ({
    generate: () => {
      handleGenerate(false);
    },
  }));

  useEffect(() => {
    const loadReportParameters = async () => {
      try {
        let response;

        if (portfolioData) {
          response = await AssistantService.fetchReportParameters(portfolioId);
        } else {
          console.warn("No portfolio data or thread/portfolio IDs available for report parameters");
          return;
        }

        setBankerName(response.banker_name || "");
        setBankerRole(response.banker_role || "");

        // Process sections to add hidden flag
        const processedSections = JSON.parse(
          JSON.stringify(response.report_sections),
        );
        setReportSections(processedSections);
      } catch (error) {
        console.error("Failed to load report parameters:", error);
      }
    };

    loadReportParameters();
  }, [threadId, portfolioId, portfolioData]);

  // Helper to check if portfolio_story section is selected
  const isPortfolioStorySelected = () => {
    return reportSections.portfolio_story?.visible && reportSections.portfolio_story?.selected;
  };

  // Helper to get current story from the global store
  const getCurrentStory = () => {
    if (!threadId || !portfolioId) return null;
    return stories?.[threadId]?.[portfolioId] ?? null;
  };

  // Helper to check if story is available
  const isStoryAvailable = () => {
    const story = getCurrentStory();
    return story !== null && story !== "" && story !== "Generazione in corso...";
  };

  // Check if PDF generation should be disabled
  const isPdfGenerationDisabled = () => {
    return isPortfolioStorySelected() && !isStoryAvailable();
  };


  const handleGenerate = (fromReport: boolean = false) => {
    // If portfolio_story is selected but story is not available, prevent generation
    if (isPdfGenerationDisabled()) {
      console.warn("PDF generation disabled: portfolio_story selected but story not available");
      return;
    }

    const data = {
      banker_name: bankerName,
      banker_role: bankerRole,
      financial_plan: artifact?.financial_plan?.toString(),
      mifid_profile: artifact?.risk_profile,
      proposed_value: proposedValue,
      ...(fromReport && { report_sections: reportSections }),
    };

    onGenerate(data, fromReport);
  };

  const hasSelectedSections = () => {
    return Object.values(reportSections).some((section) => section.visible && section.selected);
  };

  const [open, setOpen] = useState(false);
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>{children}</AlertDialogTrigger>
      <AlertDialogContent
        style={{
          backgroundColor: "white",
          color: colorsConfig.threadHistoryCopy || "#ffffff",
          maxWidth: "600px",
        }}
      >
        <AlertDialogHeader>
          <AlertDialogTitle style={{ color: colorsConfig.finecoBlack }}>
            {t("generateReport.title")}
          </AlertDialogTitle>
        </AlertDialogHeader>
        <AlertDialogDescription>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2">
                <Label
                  htmlFor="bankerRole"
                  style={{ color: colorsConfig.finecoBlack }}
                >
                  {t("generateReport.bankerRole")}
                </Label>
                <select
                  id="bankerRole"
                  value={bankerRole}
                  onChange={(e) => setBankerRole(e.target.value)}
                  className="w-full rounded border p-2"
                >
                  <option value="Personal Financial Advisor">Personal Financial Advisor</option>
                  <option value="Private Banker">Private Banker</option>
                  <option value="Senior Private Banker">Senior Private Banker</option>
                  <option value="Senior Private Banker e Ambassador">Senior Private Banker e Ambassador</option>
                  <option value="Group Manager">Group Manager</option>
                  <option value="Area Manager">Area Manager</option>
                </select>
              </div>
              <div>
                <Label
                  htmlFor="proposedValue"
                  style={{ color: colorsConfig.finecoBlack }}
                >
                  {t("generateReport.proposedValue")}
                </Label>
                <Input
                  id="proposedValue"
                  type="number"
                  value={proposedValue}
                  onChange={(e) => setProposedValue(Number(e.target.value))}
                  className="w-full text-right"
                />
              </div>
            </div>

            <div className="mt-8">
              <h3
                className="mb-4 font-medium"
                style={{ color: colorsConfig.finecoBlack }}
              >
                {t("generateReport.sections")}
              </h3>
              <ReportSectionsTree
                sections={reportSections}
                onChange={setReportSections}
              />

              {/* Story status message */}
              {isPortfolioStorySelected() && (
                <div className="mt-4 p-3 rounded border" style={{
                  backgroundColor: isPdfGenerationDisabled() ? "#fff3cd" : "#d4edda",
                  borderColor: isPdfGenerationDisabled() ? "#ffeaa7" : "#c3e6cb",
                  color: colorsConfig.finecoBlack
                }}>
                  {isPdfGenerationDisabled() ? (
                    <div>
                      <strong>{t("generateReport.storyGenerating")}</strong>
                      <br />
                      <small>{t("generateReport.storyGeneratingDescription")}</small>
                    </div>
                  ) : (
                    <div>
                      <strong>{t("generateReport.storyAvailable")}</strong>
                      <br />
                      <small>{t("generateReport.storyAvailableDescription")}</small>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </AlertDialogDescription>
        <AlertDialogFooter>

          <FinecoButton
            click={() => {
              setOpen(false);
            }}
            white={true}
            copy={t("global.cancel").toUpperCase()}
          />
          <FinecoButton
            disabled={!hasSelectedSections() || isPdfGenerationDisabled()}
            click={() => {
              handleGenerate(true);
              setOpen(false);
            }}
            copy={
              isPdfGenerationDisabled()
                ? t("generateReport.waitingForStory").toUpperCase()
                : t("generateReport.generate").toUpperCase()
            }
          />
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
});
